import "./facilityDetailsScreen.scss";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import React, {useCallback, useEffect, useState} from "react";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import AdminDetailsViewEditComponent from "./admin-details-view-edit/adminDetailsViewEditComponent";
import FacilityDetailsViewEditComponent from "./facility-details-view-edit/facilityDetailsViewEditComponent";
import PhysicianDetailsViewEditComponent from "./physician-details-view-edit/physicianDetailsViewEditComponent";
import PrimaryContactDetailsViewEditComponent
    from "./primary-contact-details-view-edit/primaryContactDetailsViewEditComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {ImageConfig} from "../../../constants";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import FacilityUserViewEditComponent from "./facility-user-view-edit/FacilityUserViewEditComponent";

interface FacilityDetailsScreenProps {

}

const ADMIN_DETAILS = 'admin_details';
const USER_DETAILS = 'user_details';
const FACILITY_DETAILS = 'facility_details';
const PRIMARY_CONTACT = 'primary_contact';
const PROVIDER_OR_PHYSICIAN = 'provider_or_physician';

const FacilityDetailsScreen = (props: FacilityDetailsScreenProps) => {
    const [activeTab, setActiveTab] = useState<"admin_details" | "facility_details" | "primary_contact" | "provider_or_physician" | "panel_details" | "user_details">(ADMIN_DETAILS);
    const navigate = useNavigate();
    const params = useParams<{ facilityId: string | undefined }>();
    const {facilityId} = params;
    const [searchParams, setSearchParams] = useSearchParams();
    const [facilityDetails, setFacilityDetails] = useState<any>(undefined);
    const [isFacilityDetailsLoading, setIsFacilityDetailsLoading] = useState<boolean>(false);
    const [isFacilityDetailsLoaded, setIsFacilityDetailsLoaded] = useState<boolean>(false);
    const [isFacilityDetailsLoadingFailed, setIsFacilityDetailsLoadingFailed] = useState<boolean>(false);
    const [facilityDetailsMode, setFacilityDetailsMode] = useState<"view" | "edit">("view");
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setCanEdit(commonService.checkAccess('Facility',
            "facilityEdit", currentUser?.userAccessModules))
        setActiveTab(searchParams.get("currentTab") as typeof activeTab || ADMIN_DETAILS)
    }, [currentUser, searchParams]);

    useEffect(() => {
        if (facilityId) {
            dispatch(setCurrentNavParams("Back", "Facility", CommonService._routeConfig.FacilityListRoute()));
            dispatch(setSideMenuView('compact'));
        }
    }, [facilityId, dispatch, navigate]);

    const getFacilityDetails = useCallback(() => {
        const payload = {};
        let facilityDetails: any = {};
        setIsFacilityDetailsLoading(true);
        setIsFacilityDetailsLoaded(false);
        setIsFacilityDetailsLoadingFailed(false);
        CommonService._facility.getFacilityDetails(facilityId, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    facilityDetails = response.data;
                }
                setFacilityDetails(facilityDetails);
                setIsFacilityDetailsLoading(false);
                setIsFacilityDetailsLoaded(true);
                setIsFacilityDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setFacilityDetails(facilityDetails);
                setIsFacilityDetailsLoading(false);
                setIsFacilityDetailsLoaded(false);
                setIsFacilityDetailsLoadingFailed(true);
            });
    }, [facilityId]);

    const handleAdminDetailsUpdate = useCallback((facilityAdminDetails: any) => {
        setFacilityDetails((prev: any) => {
            return {
                ...prev,
                ...facilityAdminDetails
            }
        });
    }, []);

    const handleFacilityBasicInformationDetailsUpdate = useCallback((facilityInformation: any) => {
        setFacilityDetails(() => {
            return {
                ...facilityInformation
            }
        })
    }, []);

    const handleFacilityPrimaryContactDetailsUpdate = useCallback((primaryContactDetails: any) => {
        setFacilityDetails(() => {
            return {
                ...facilityDetails,
                ...primaryContactDetails
            }
        })
    }, [facilityDetails]);


    useEffect(() => {
        if (facilityId) {
            getFacilityDetails();
        }
    }, [facilityId, getFacilityDetails]);

    const handleActiveTabChange = useCallback((value: any) => {
        if (facilityDetailsMode === 'edit') {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Discard",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                setActiveTab(value);
            }).catch(() => {

            })
        } else {
            setActiveTab(value);
        }
        searchParams.set("currentTab", value);
        setSearchParams(searchParams);
    }, [facilityDetailsMode, searchParams, setSearchParams])


    return (
        <div className={'facility-details-component details-screen screen'}>
            {
                isFacilityDetailsLoading && <div className="h-v-center">
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
            {
                isFacilityDetailsLoadingFailed && <>
                    <StatusCardComponent title={"Facility details failed to load"}/>
                </>
            }
            {
                isFacilityDetailsLoaded && <>
                    <div className={'details-module-title'}>Facility Details</div>
                    <div className="basic-details-wrapper">
                        <div className="details-screen-header-status-section">
                            <div className="details-screen-header">
                                <HeadingComponent
                                    title={`${CommonService.capitalize(facilityDetails?.name)}`}
                                    statusLabel={facilityDetails?.status}
                                />
                            </div>
                        </div>
                        <div className="details-screen-sub-header">
                            <DetailsDataKeyValueComponent
                                title={"Facility Type:"}
                                isHorizontal={true}> {facilityDetails?.type} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Added by:"}
                                isHorizontal={true}> {(facilityDetails?.createdByDetails?.firstName ? facilityDetails?.createdByDetails?.firstName : "-") + " " + (facilityDetails?.createdByDetails?.lastName ? facilityDetails?.createdByDetails?.lastName : "")} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Created on:"}
                                isHorizontal={true}>
                                <>
                                    {commonService.formatDate(facilityDetails?.createdAt)}
                                    &nbsp;|&nbsp;
                                    {commonService.formatTime(facilityDetails?.createdAt)}
                                </>

                            </DetailsDataKeyValueComponent>
                        </div>
                    </div>
                    <div className="clear-fix"/>
                    <div className="details-screen-body">
                        <TabsWrapperComponent>
                            <TabsComponent
                                value={activeTab}
                                allowScrollButtonsMobile={false}
                                onUpdate={(e: any, v: any) => {
                                    handleActiveTabChange(v);
                                }}
                                variant={"fullWidth"}
                            >
                                <TabComponent label="ADMIN DETAILS" value={ADMIN_DETAILS}/>
                                <TabComponent label={"USER DETAILS"} value={USER_DETAILS}/>
                                <TabComponent label="FACILITY DETAILS" value={FACILITY_DETAILS}/>
                                <TabComponent label="PRIMARY CONTACT" value={PRIMARY_CONTACT}/>
                                <TabComponent label="PROVIDER/PHYSICIAN" value={PROVIDER_OR_PHYSICIAN}/>
                            </TabsComponent>
                            <TabContentComponent selectedTab={activeTab} value={ADMIN_DETAILS}>
                                <AdminDetailsViewEditComponent
                                    facilityDetails={facilityDetails}
                                    onUpdateAdminDetails={handleAdminDetailsUpdate}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setFacilityDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={USER_DETAILS}>
                                <FacilityUserViewEditComponent
                                    facilityDetails={facilityDetails}
                                    onUpdateAdminDetails={setFacilityDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setFacilityDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={FACILITY_DETAILS}>
                                <FacilityDetailsViewEditComponent
                                    facilityDetails={facilityDetails}
                                    canEdit={canEdit}
                                    onUpdateFacilityInformation={handleFacilityBasicInformationDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setFacilityDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={PRIMARY_CONTACT}>
                                <PrimaryContactDetailsViewEditComponent
                                    facilityDetails={facilityDetails}
                                    canEdit={canEdit}
                                    onUpdatePrimaryContactDetails={handleFacilityPrimaryContactDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setFacilityDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={PROVIDER_OR_PHYSICIAN}>
                                <PhysicianDetailsViewEditComponent
                                    facilityDetails={facilityDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setFacilityDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>
                        </TabsWrapperComponent>
                    </div>
                </>
            }
        </div>
    );

};

export default FacilityDetailsScreen;
